
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DataTable } from './DataTable';
import { TicketVolumeChart } from './TicketVolumeChart';

interface Incident {
  number?: string;
  incident_number?: string;
  short_description?: string;
  description?: string;
  priority?: string;
  status?: string;
  state?: string;
  created?: string;
  created_at?: string;
  [key: string]: any;
}

interface HighPriorityIncidentsProps {
  incidents: Incident[];
  showFullTable?: boolean;
}

export function HighPriorityIncidents({ incidents, showFullTable = false }: HighPriorityIncidentsProps) {
  const highPriorityIncidents = React.useMemo(() => {
    if (showFullTable) {
      // When showing full table, return all incidents
      return incidents;
    }
    
    return incidents.filter(incident => {
      const priority = (incident.priority || "").toLowerCase();
      const state = (incident.status || incident.state || "").toLowerCase();
      
      const isHighPriority = priority === "high" || 
                             priority === "critical" || 
                             priority === "1" || 
                             priority === "2";
      
      const isNotClosed = !state.includes("closed") && 
                          !state.includes("resolved") &&
                          !state.includes("completed");
                          
      return isHighPriority && isNotClosed;
    });
  }, [incidents, showFullTable]);

  const columns = [
    {
      key: "number",
      header: "Incident #",
      cell: (item: Incident) => (
        <span className="font-medium text-sm">
          {item.number || item.incident_number || "N/A"}
        </span>
      ),
    },
    {
      key: "short_description",
      header: "Description",
      cell: (item: Incident) => (
        <div className="max-w-xs truncate text-sm">
          {item.short_description || item.description || "No description"}
        </div>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      cell: (item: Incident) => {
        const priority = (item.priority || "").toLowerCase();
        let badgeVariant: "default" | "secondary" | "destructive" | "outline" = "outline";
        
        if (priority === "critical" || priority === "1") {
          badgeVariant = "destructive";
        } else if (priority === "high" || priority === "2") {
          badgeVariant = "default";
        } else if (priority === "medium" || priority === "3") {
          badgeVariant = "secondary";
        }
        
        return (
          <Badge variant={badgeVariant} className="capitalize">
            {item.priority || "Unknown"}
          </Badge>
        );
      },
    },
    {
      key: "created",
      header: "Created",
      cell: (item: Incident) => {
        const date = new Date(item.created || item.created_at || "");
        return (
          <span className="text-sm text-muted-foreground">
            {isNaN(date.getTime())
              ? "Unknown"
              : date.toLocaleDateString() + " " + date.toLocaleTimeString()}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      cell: (item: Incident) => {
        const status = (item.status || item.state || "").toLowerCase();
        let badgeVariant: "default" | "secondary" | "destructive" | "outline" = "outline";
        
        if (status.includes("open") || status.includes("active")) {
          badgeVariant = "default";
        } else if (status.includes("resolved") || status.includes("closed")) {
          badgeVariant = "secondary";
        } else if (status.includes("critical") || status.includes("major")) {
          badgeVariant = "destructive";
        }
        
        return (
          <Badge variant={badgeVariant} className="capitalize">
            {item.status || item.state || "Unknown"}
          </Badge>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <TicketVolumeChart 
          data={incidents} 
          title="Incident Volume Over Time" 
        />
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>
              {showFullTable ? "All Incidents" : "High Priority Incidents"}
            </CardTitle>
            <Badge variant="outline" className="ml-2">
              {highPriorityIncidents.length} incident{highPriorityIncidents.length !== 1 ? 's' : ''}
            </Badge>
          </CardHeader>
          <CardContent>
            <DataTable 
              data={highPriorityIncidents} 
              columns={columns} 
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
